import React, { useState } from "react";
import {
  FiUser,
  FiHash,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import useApi from "../../../CustomHooks/useApi";

const AccountVerificationSection = ({ onSave, onEdit }) => {
  const [isOpen, setIsOpen] = useState(true);

  const [form, setForm] = useState({
    accountHolderName: "",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
  });

  const [errors, setErrors] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);
  const [bankVerified, setBankVerified] = useState(false);

  const [isSaved, setIsSaved] = useState(false);
  const [isReEnterBank, setIsReEnterBank] = useState(false);

  const { callApi: bankVerify } = useApi(
    "/staff/onboard/verify/bene",
    "POST"
  );

  /* ---------------- VALIDATION ---------------- */

  const validate = () => {
    const e = {};
    if (!form.accountHolderName) e.accountHolderName = "Required";
    if (!form.accountNumber) e.accountNumber = "Required";
    if (!form.ifscCode) e.ifscCode = "Required";
    if (!form.bankName) e.bankName = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ---------------- VERIFY / RE-VERIFY BANK ---------------- */

  const verifyBank = async () => {
    if (!validate()) return;

    try {
      setIsVerifying(true);

      const payload = {
        beneficiaryAccount: form.accountNumber,
        beneficiaryIFSC: form.ifscCode,
        beneficiaryName: form.accountHolderName,
        nameMatchScore: "1",
        nameFuzzy: true,
      };

      const res = await bankVerify(payload);

      if (res === true) {
        setBankVerified(true);
        setIsReEnterBank(false); // back to normal verified state
      } else {
        setErrors({ accountNumber: "Bank verification failed" });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  /* ---------------- SAVE ---------------- */

  const handleSave = () => {
    if (!bankVerified) return;
    setIsSaved(true);
    onSave(form);
  };

  /* ---------------- EDIT ---------------- */

  const handleEdit = () => {
    setIsSaved(false);
    onEdit();
  };

  /* ---------------- RE-ENTER BANK ---------------- */

  const handleReEnterBank = () => {
    onEdit();
    setIsReEnterBank(true);
    setBankVerified(false);
    setIsSaved(false); // 🔥 IMPORTANT FIX
    setErrors({});
    setForm({
      accountHolderName: "",
      accountNumber: "",
      ifscCode: "",
      bankName: "",
    });
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* HEADER */}
      <div
        className="flex cursor-pointer items-center justify-between border-b px-6 py-4"
        onClick={() => setIsOpen((p) => !p)}
      >
        <h3 className="text-sm font-semibold text-gray-800">
          Bank Account Verification
        </h3>
        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
      </div>

      {isOpen && (
        <div className="p-6">
          {/* INPUT GRID */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Account Holder */}
            <div>
              <label className="text-xs text-gray-600">
                Account Holder Name *
              </label>
              <div className="relative mt-1">
                <FiUser className="absolute left-3 top-3 text-gray-400" />
                <input
                  disabled={bankVerified && !isReEnterBank}
                  className="w-full rounded-md border px-9 py-2 text-sm"
                  value={form.accountHolderName}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      accountHolderName: e.target.value,
                    })
                  }
                />
              </div>
              {errors.accountHolderName && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.accountHolderName}
                </p>
              )}
            </div>

            {/* Account Number */}
            <div>
              <label className="text-xs text-gray-600">
                Account Number *
              </label>
              <div className="relative mt-1">
                <FiHash className="absolute left-3 top-3 text-gray-400" />
                <input
                  disabled={bankVerified && !isReEnterBank}
                  className="w-full rounded-md border px-9 py-2 text-sm"
                  value={form.accountNumber}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      accountNumber: e.target.value.replace(/\D/g, ""),
                    })
                  }
                />
                {bankVerified && (
                  <FiCheckCircle className="absolute right-3 top-3 text-green-600" />
                )}
              </div>
              {errors.accountNumber && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.accountNumber}
                </p>
              )}
            </div>

            {/* IFSC */}
            <div>
              <label className="text-xs text-gray-600">
                IFSC Code *
              </label>
              <input
                disabled={bankVerified && !isReEnterBank}
                className="w-full rounded-md border px-3 py-2 text-sm mt-1"
                value={form.ifscCode}
                onChange={(e) =>
                  setForm({
                    ...form,
                    ifscCode: e.target.value.toUpperCase(),
                  })
                }
              />
              {errors.ifscCode && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.ifscCode}
                </p>
              )}
            </div>

            {/* Bank Name */}
            <div>
              <label className="text-xs text-gray-600">
                Bank Name *
              </label>
              <input
                disabled={bankVerified && !isReEnterBank}
                className="w-full rounded-md border px-3 py-2 text-sm mt-1"
                value={form.bankName}
                onChange={(e) =>
                  setForm({ ...form, bankName: e.target.value })
                }
              />
              {errors.bankName && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.bankName}
                </p>
              )}
            </div>
          </div>

          {/* BUTTONS */}
          <div className="mt-6">
            {/* VERIFY / RE-VERIFY */}
            {!bankVerified && (
              <button
                onClick={verifyBank}
                disabled={isVerifying}
                className="rounded-full bg-[#0A2A4F] px-8 py-2 text-sm text-white"
              >
                {isVerifying ? "Verifying..." : "Verify Bank"}
              </button>
            )}

            {/* RE-ENTER */}
            {isSaved && !isReEnterBank && (
              <button
                onClick={handleReEnterBank}
                className="ml-3 rounded-full bg-gray-500 px-8 py-2 text-sm text-white"
              >
                Re-Enter Bank
              </button>
            )}

            {/* SAVE / EDIT */}
            {bankVerified && !isReEnterBank && (
              <div className="mt-6">
                {!isSaved ? (
                  <button
                    onClick={handleSave}
                    className="rounded-full bg-green-600 px-8 py-2 text-sm text-white"
                  >
                    Save
                  </button>
                ) : (
                  <button
                    onClick={handleEdit}
                    className="rounded-full bg-gray-500 px-8 py-2 text-sm text-white"
                  >
                    Edit
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountVerificationSection;
